import { ShareButton } from '@app/components/Guide/ui/ShareButton'
import { EmailOutlined, LinkedIn } from '@mui/icons-material'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@src/components/ui/tooltip'
import { format } from 'date-fns'
import { useParams } from 'react-router-dom'
import { Author, GuideMainTopics, HeroImage } from '../types/guide.types'
import StrapiImage from '../ui/StrapiImage'

interface ArticlePageHeaderProps {
  title: string
  subTitle?: string
  author?: Author[]
  publishedAt?: string
  read_time?: number | string
  articleType?: GuideMainTopics
  hero_image?: HeroImage['formats']['small']['url']
}
export const ArticlePageHeader = ({
  title,
  author,
  publishedAt,
  read_time,
  hero_image,
}: ArticlePageHeaderProps) => {
  const { mainTopic } = useParams()
  if (!title) return null

  const authors = author ?? []

  if (mainTopic === GuideMainTopics.METHODS || mainTopic === GuideMainTopics.FUNDAMENTALS) {
    return (
      <section className="flex flex-col items-start self-stretch w-full gap-8 py-16 pb-24 pr-8 text-left">
        <h2 className="m-0 w-full text-left text-2xl font-bold tracking-tight text-foreground sm:text-3xl lg:text-[1.75rem]">
          {title}
        </h2>
        <ShareButton />
      </section>
    )
  }

  if (mainTopic === GuideMainTopics.INSIGHTS) {
    return (
      <header className="mt-5 mb-20 overflow-hidden border shadow-sm rounded-2xl border-border bg-card text-card-foreground">
        <div className="p-4 bg-muted/40 sm:p-6 lg:p-8">
          <div className="overflow-hidden border shadow-sm rounded-xl border-border bg-background">
            <StrapiImage src={hero_image} />
          </div>
        </div>

        <div className="px-4 pt-6 pb-8 space-y-6 sm:px-6 lg:px-8 lg:pb-10">
          <div className="max-w-3xl space-y-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Insight</p>

            <h1 className="text-xl font-bold tracking-tight text-balance text-foreground sm:text-4xl lg:text-3xl">
              {title}
            </h1>

            <div className="flex flex-wrap items-center text-sm gap-x-3 gap-y-2 text-muted-foreground">
              {authors.length > 0 && (
                <span>
                  By{' '}
                  {authors.map((item, index) => (
                    <span key={item.documentId}>
                      <strong className="font-semibold text-foreground">{item.name}</strong>
                      {index < authors.length - 1 ? ', ' : ''}
                    </span>
                  ))}
                </span>
              )}
              {authors.length > 0 && <span className="text-border">•</span>}
              {publishedAt && <span>{format(new Date(publishedAt), 'MMMM d, yyyy')}</span>}
              {publishedAt && read_time && <span className="text-border">•</span>}
              {read_time && <span>{read_time} minutes</span>}
            </div>
          </div>

          <TooltipProvider>
            <div className="flex flex-wrap items-center gap-3 pt-6 border-t border-border">
              {authors
                .filter((item) => item.social_links?.some((link) => link.platform === 'linkedin'))
                .map((item) => {
                  const linkedinLink = item.social_links?.find(
                    (link) => link.platform === 'linkedin',
                  )?.url

                  return (
                    <Tooltip key={`linkedin-${item.documentId}`}>
                      <TooltipTrigger asChild>
                        <a
                          className="inline-flex items-center justify-center transition-colors border rounded-full shadow-sm iconLink size-9 border-border bg-background text-primary hover:bg-primary hover:text-primary-foreground"
                          href={linkedinLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${item.name}'s LinkedIn profile`}
                        >
                          <LinkedIn className="text-xl" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{item.name} LinkedIn</p>
                      </TooltipContent>
                    </Tooltip>
                  )
                })}

              {authors.map((item) => (
                <Tooltip key={`email-${item.documentId}`}>
                  <TooltipTrigger asChild>
                    <a
                      className="inline-flex items-center justify-center transition-colors border rounded-full shadow-sm iconLink size-9 border-border bg-background text-primary hover:bg-primary hover:text-primary-foreground"
                      href={`mailto:${item.email}`}
                      aria-label={`Email ${item.name}`}
                    >
                      <EmailOutlined className="text-xl" />
                    </a>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{item.name}</p>
                  </TooltipContent>
                </Tooltip>
              ))}

              <ShareButton />
            </div>
          </TooltipProvider>
        </div>
      </header>
    )
  }

  return null
}
